import ERC20_ABI from 'abis/erc20.json'
import { useConnection } from 'hooks/useConnection'
import { useDebouncedCallback } from 'hooks/useDebounce'
import { useState } from 'react'
import { Address, isAddress } from 'viem'

interface TokenInfo {
  symbol: string
  name: string
  decimals: number
}

export function useTokenInfo() {
  const { wagmiPublicClient } = useConnection()
  const [tokenInfo, setTokenInfo] = useState<TokenInfo | null>(null)
  const [error, setError] = useState<string | null>(null)

  const fetchTokenInfo = useDebouncedCallback(async (address: string | null) => {
    setError(null)
    setTokenInfo(null)

    if (!address || !isAddress(String(address))) {
      return
    }

    if (!wagmiPublicClient) {
      setError('No wagmiPublicClient available')
      return
    }

    try {
      // Fetch token info using multicall for better performance
      const results = await wagmiPublicClient.multicall({
        contracts: [
          {
            address: address as Address,
            abi: ERC20_ABI,
            functionName: 'symbol',
          },
          {
            address: address as Address,
            abi: ERC20_ABI,
            functionName: 'name',
          },
          {
            address: address as Address,
            abi: ERC20_ABI,
            functionName: 'decimals',
          },
        ],
      })

      const [symbolResult, nameResult, decimalsResult] = results

      if (symbolResult.status === 'success' && nameResult.status === 'success' && decimalsResult.status === 'success') {
        setTokenInfo({
          symbol: symbolResult.result as string,
          name: nameResult.result as string,
          decimals: decimalsResult.result as number,
        })
      } else {
        throw new Error('Failed to fetch token info')
      }
    } catch (error) {
      console.error('Failed to fetch token info:', error)
      setError('Invalid token address')
    }
  }, 1000)

  const clearTokenInfo = () => {
    setTokenInfo(null)
    setError(null)
  }

  return {
    tokenInfo,
    error,
    fetchTokenInfo,
    clearTokenInfo,
  }
}
